import env from '../utils/env';
import { 
  Integration, 
  CreateIntegrationBackendDto,
  UpdateIntegrationBackendDto
} from '../types/integration';


const API_URL = env.apiUrl;

interface ApiError {
  error: string;
  code?: string;
  details?: Record<string, unknown>;
}

interface ApiResponse<T> {
  data?: T;
  error?: ApiError;
  message?: string; // For 202 Accepted responses
}

class ApiService {
  private static instance: ApiService;
  private token: string | null = null;

  private constructor() {
  }

  static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  setToken(token: string | null): void {
    this.token = token;
  }

  private async fetchWithAuth<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers = new Headers({
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    });

    // List of endpoints that don't require authentication
    const publicEndpoints = [
      '/auth/magic-link',
      '/templates',
      '/templates/',
      '/integrations',
      '/integrations/',
      '/integrations/categories',
      '/projects/preview/',
      '/projects/:id/public-access'
    ];

    // Check if the current endpoint is public or starts with any public endpoint
    let isPublicEndpoint = publicEndpoints.some(publicPath => 
      endpoint === publicPath || 
      endpoint.startsWith(publicPath)
    );
    
    // Special case for project endpoints with preview parameter
    // Extract preview token from the endpoint URL if it exists
    let previewToken: string | null = null;
    
    // Check if the endpoint itself contains a preview parameter
    if (endpoint.includes('?preview=') || endpoint.includes('&preview=')) {
      const endpointUrl = new URL(`http://example.com${endpoint}`);
      previewToken = endpointUrl.searchParams.get('preview');
    }
    
    // If no preview token in endpoint, check the current page URL
    if (!previewToken) {
      const urlParams = new URLSearchParams(window.location.search);
      previewToken = urlParams.get('preview');
    }
    
    const isProjectWithPreview = endpoint.startsWith('/projects/') && previewToken;
    
    if (isProjectWithPreview) {
      isPublicEndpoint = true;
    }

    // Only require token for non-public endpoints
    if (!this.token && !isPublicEndpoint) {
      throw new Error('No authentication token available');
    }

    if (this.token) {
      headers.set('Authorization', `Bearer ${this.token}`);
    }

    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers,
      });

      // Handle 204 No Content responses (like from DELETE operations)
      if (response.status === 204) {
        return undefined as unknown as T;
      }

      // For other responses, try to parse JSON
      let data: ApiResponse<T>;
      try {
        data = await response.json();
      } catch (jsonError) {
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }
        // If response is OK but not JSON, return undefined
        return undefined as unknown as T;
      }

      // Special handling for 202 Accepted responses (e.g., magic link sent)
      if (response.status === 202 && data.message) {
        // Return the message directly
        return { message: data.message } as unknown as T;
      }
      
      if (!response.ok || data.error) {
        // If we get a 401, clear the token
        if (response.status === 401) {
          this.token = null;
        }
        
        const error = new Error(data.error?.error || `Request failed: ${response.status}`);
        // Add error code if available
        if (data.error?.code) {
          (error as any).code = data.error.code;
        }
        
        // Add the response to the error for special handling
        (error as any).response = {
          status: response.status,
          data: data
        };
        
        throw error;
      }

      return data.data as T;
    } catch (error) {
      // Ensure token is cleared on network errors too
      if (error instanceof Error && error.message.includes('401')) {
        this.token = null;
      }
      throw error;
    }
  }

  // Auth endpoints
  async verifyToken(token: string): Promise<void> {
    await this.fetchWithAuth('/auth/verify', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  async sendMagicLink(email: string, projectId?: string): Promise<void> {
    return this.fetchWithAuth('/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email, projectId }),
    });
  }

  // Project endpoints
  async createProject(data: {
    name: string;
    description?: string;
    nodes: any[];
    edges: any[];
    owner_id?: string;
    shared_with?: string[];
    is_locked?: boolean;
    is_archived?: boolean;
    version?: number;
    settings?: Record<string, unknown>;
  }): Promise<any> {
    return this.fetchWithAuth('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getProjects(showAll: boolean = false): Promise<any[]> {
    const endpoint = showAll ? '/projects?all=true' : '/projects';
    return this.fetchWithAuth(endpoint);
  }

  async getProject(id: string): Promise<any> {
    // Get preview token from URL if it exists
    const urlParams = new URLSearchParams(window.location.search);
    const previewToken = urlParams.get('preview');
    
    // Include preview token in the request if it exists
    const endpoint = previewToken 
      ? `/projects/${id}?preview=${previewToken}` 
      : `/projects/${id}`;
    
    
    try {
      // If we have a preview token, always include it in the headers as well
      const options: RequestInit = previewToken ? {
        headers: {
          'X-Preview-Token': previewToken
        }
      } : {};
      
      // First attempt with fetchWithAuth if we have a token
      if (this.token) {
        try {
          return await this.fetchWithAuth(endpoint, options);
        } catch (authError) {
          // If we get an auth error but have a preview token, try without auth
          if (previewToken && authError instanceof Error && 
              (authError.message.includes('401') || 
               authError.message.includes('No authentication token available'))) {
            // Continue to the fallback approaches below
          } else {
            // Not a preview-related auth error, re-throw
            throw authError;
          }
        }
      }
      
      // If we don't have a token or auth failed with preview token, try direct fetch
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(previewToken ? { 'X-Preview-Token': previewToken } : {})
        }
      });
      
      if (!response.ok) {
        console.error(`ApiService: Direct fetch failed with status ${response.status}`);
        throw new Error(`Request failed: ${response.status}`);
      }
      
      const data = await response.json();
      if (data && data.data) {
        return data.data;
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      throw error;
    }
  }

  async updateProject(id: string, data: Partial<any>): Promise<any> {
    return this.fetchWithAuth(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProject(id: string): Promise<void> {
    return this.fetchWithAuth(`/projects/${id}`, {
      method: 'DELETE',
    });
  }

  // Collaborator endpoints
  async addCollaborator(
    projectId: string,
    data: { userId: string; role: 'editor' | 'viewer' }
  ): Promise<any> {
    try {
      return await this.fetchWithAuth(`/projects/${projectId}/collaborators`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error: any) {
      // Check if the error response contains a message about sending a magic link
      if (error.response && error.response.status === 202 && 
          error.response.data && error.response.data.message && 
          error.response.data.message.includes('magic link has been sent')) {
        // Return the special message to the caller
        return { message: error.response.data.message };
      }
      
      // Re-throw other errors
      throw error;
    }
  }

  async removeCollaborator(projectId: string, userId: string): Promise<void> {
    return this.fetchWithAuth(`/projects/${projectId}/collaborators/${userId}`, {
      method: 'DELETE',
    });
  }

  // Project locking endpoints
  async lockProject(id: string): Promise<any> {
    return this.fetchWithAuth(`/projects/${id}/lock`, {
      method: 'PUT',
    });
  }

  async unlockProject(id: string): Promise<any> {
    return this.fetchWithAuth(`/projects/${id}/unlock`, {
      method: 'PUT',
    });
  }
  
  // Project public access endpoints
  async getProjectPublicAccess(id: string): Promise<{ accessLevel: 'No Access' | 'Can View' | 'Can Edit' }> {
    return this.fetchWithAuth(`/projects/${id}/public-access`);
  }

  async setProjectPublicAccess(id: string, accessLevel: 'No Access' | 'Can View' | 'Can Edit'): Promise<any> {
    return this.fetchWithAuth(`/projects/${id}/public-access`, {
      method: 'PUT',
      body: JSON.stringify({ accessLevel }),
    });
  }

  // Import/Export endpoints
  async exportProject(id: string): Promise<any> {
    return this.fetchWithAuth(`/import-export/${id}/export`);
  }

  async importProject(data: {
    name: string;
    description?: string;
    nodes: any[];
    edges: any[];
  }): Promise<any> {
    return this.fetchWithAuth('/import-export/import', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Template endpoints
  async getTemplates(search?: string): Promise<any[]> {
    const searchParams = new URLSearchParams();
    if (search) {
      searchParams.append('search', search);
    }
    const queryString = searchParams.toString();
    const endpoint = `/templates${queryString ? `?${queryString}` : ''}`;
    return this.fetchWithAuth(endpoint);
  }

  async getTemplate(id: string): Promise<any> {
    return this.fetchWithAuth(`/templates/${id}`);
  }

  async createTemplate(data: {
    name: string;
    description: string;
    category: string;
    nodes: any[];
    edges: any[];
    preview?: string;
  }): Promise<any> {
    return this.fetchWithAuth('/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTemplate(id: string, data: Partial<any>): Promise<any> {
    return this.fetchWithAuth(`/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTemplate(id: string): Promise<void> {
    return this.fetchWithAuth(`/templates/${id}`, {
      method: 'DELETE',
    });
  }

  // Integration endpoints
  async getIntegrations(params?: {
    page?: number;
    pageSize?: number;
    category?: string;
    search?: string;
    enabled?: boolean;
  }): Promise<{ data: Integration[]; hasMore: boolean }> {
    const searchParams = new URLSearchParams();
    // Backend pagination is 0-indexed for 'page', but we might want 1-indexed for API calls
    // Let's assume backend expects 0-indexed 'page' and 'pageSize'
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    if (params?.category) searchParams.append('category', params.category);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.enabled !== undefined) searchParams.append('enabled', params.enabled.toString());

    const queryString = searchParams.toString();
    const endpoint = `/integrations${queryString ? `?${queryString}` : ''}`;

    // Assuming fetchWithAuth returns the raw data array
    const fetchedData = await this.fetchWithAuth<Integration[]>(endpoint);

    // Determine if there's more data based on pageSize
    const hasMore = params?.pageSize ? fetchedData.length === params.pageSize : false;

    return {
      data: fetchedData,
      hasMore: hasMore,
    };
  }

  async getIntegration(id: string): Promise<any> {
    return this.fetchWithAuth(`/integrations/${id}`);
  }

  async createIntegration(data: CreateIntegrationBackendDto): Promise<Integration> {
    return this.fetchWithAuth('/integrations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateIntegration(
    id: string, 
    data: UpdateIntegrationBackendDto
  ): Promise<Integration> {

    return this.fetchWithAuth(`/integrations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' }
    });
  }

  async deleteIntegration(id: string): Promise<void> {
    return this.fetchWithAuth(`/integrations/${id}`, {
      method: 'DELETE',
    });
  }

  async validateIntegrationSettings(id: string, settings: Record<string, unknown>): Promise<any> {
    return this.fetchWithAuth(`/integrations/${id}/validate`, {
      method: 'POST',
      body: JSON.stringify({ settings }),
    });
  }

  async testIntegrationConnection(id: string): Promise<any> {
    return this.fetchWithAuth(`/integrations/${id}/test`, {
      method: 'POST',
    });
  }

  async getIntegrationCategories(): Promise<string[]> {
    return this.fetchWithAuth('/integrations/categories');
  }

  // Report endpoints
  async getProjectReportDetails(projectId: string): Promise<any> {
    return this.fetchWithAuth(`/api/project/${projectId}/report`);
  }

  async updateProjectReportDetails(projectId: string, reportData: any): Promise<any> {
    return this.fetchWithAuth(`/api/project/${projectId}/report`, {
      method: 'POST',
      body: JSON.stringify(reportData),
    });
  }

  async getSalesContacts(): Promise<Array<{ id: string; email: string; name?: string }>> {
    return this.fetchWithAuth('/api/sales-contacts');
  }
}

export const apiService = ApiService.getInstance();
